import {
    MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import monitor, {
    AlertData,
    DeviceOrientation,
    EnabledVitalSigns,
    FaceSessionOptions,
    HealthMonitorCodes,
    HealthMonitorSession,
    ImageValidity,
    OfflineMeasurements,
    SessionState,
    VitalSigns,
    VitalSignsResults,
} from '@zakhealth/web-sdk';
import { InfoType, InfoData } from '../types';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Constants } from 'constants/api';
import { describeError, logger, memorySnapshot } from 'utils/logger';

// Errors from monitor.initialize() that are worth retrying:
// 7006 - wasm/models failed to download or load (often a flaky mobile network)
// 7014 - the 512 MB wasm memory could not be allocated (memory from a previous
//        page/scan may still be in the process of being released)
const RETRYABLE_INIT_CODES = [
    HealthMonitorCodes.INITIALIZATION_CODE_SDK_LOAD_FAILURE,
    HealthMonitorCodes.INITIALIZATION_CODE_INITIAL_MEMORY_ALLOCATION_ERROR,
];
const INIT_RETRY_DELAYS_MS = [1500, 3000];

// The SDK loads its wasm once per page, but concurrent initialize() calls can
// each start their own load (and 512 MB allocation), so run them one at a time.
let initQueue: Promise<unknown> = Promise.resolve();

// Number of face sessions created since this page loaded (repeat scans).
let sessionsCreatedThisPage = 0;

const codeName = (code: number) => HealthMonitorCodes[code] ?? 'UNKNOWN';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const useMonitor = (
    video: MutableRefObject<HTMLVideoElement>,
    cameraId: string,
    processingTime: number,
    licenseKey: string,
    productId: string,
    startMeasuring: boolean,
) => {
    const [session, setSession] = useState<HealthMonitorSession>();
    const sessionRef = useRef<HealthMonitorSession>();
    // SDK callbacks can still fire after leaving the scan page (e.g. the
    // TERMINATED state change); skip React state updates once unmounted.
    const isUnmounted = useRef(false);
    const [sessionState, setSessionState] = useState<SessionState>();
    const [isMonitorReady, setIsMonitorReady] = useState<boolean>();
    const [enabledVitalSigns, setEnabledVitalSigns] = useState<
        EnabledVitalSigns
    >();
    const [offlineMeasurements, setOfflineMeasurements] = useState<
        OfflineMeasurements
    >();
    const [vitalSigns, setVitalSigns] = useState<VitalSigns | null>();

    const [error, setError] = useState<AlertData>({ code: -1 });
    const [warning, setWarning] = useState<AlertData>({ code: -1 });
    const [info, setInfo] = useState<InfoData>({ type: InfoType.NONE });
    const isDismissing = useRef<boolean>(false);
    const navigate = useNavigate();

    const setInfoWithDismiss = useCallback(
        (info: InfoData, seconds?: number) => {
            if (!isDismissing.current) {
                setInfo(info);
                if (seconds) {
                    isDismissing.current = true;
                    setTimeout(() => {
                        setInfo({ type: InfoType.NONE });
                        isDismissing.current = false;
                    }, seconds * 1000);
                }
            }
        },
        [InfoType, setInfo, info, isDismissing, isDismissing.current],
    );

    const updateVitalSigns = useCallback((vitalSigns) => {
        setVitalSigns((prev) => ({
            ...prev,
            ...vitalSigns,
        }));
    }, []);

    const onVitalSign = useCallback((vitalSign: VitalSigns) => {
        if (isUnmounted.current) return;
        updateVitalSigns(vitalSign);
    }, []);

    const onFinalResults = useCallback((vitalSignsResults: VitalSignsResults) => {
        setVitalSigns(null);
        console.log('onFinalResults', vitalSignsResults);
        const userDetails = localStorage.getItem("userDetails");
        const rawClient = localStorage.getItem("clientDetails");
        const clientDetails = rawClient ? JSON.parse(rawClient) : null;
        const userData = JSON.parse(userDetails);
        let totalResultCount = vitalSignsResults.results ? Object.keys(vitalSignsResults.results).length : 0;
        logger.info('scan_finished', { resultCount: totalResultCount });
        if (totalResultCount >= 5 && userData) {
            const formData = new FormData();
            formData.append("data", JSON.stringify(vitalSignsResults.results));
            formData.append("user_id", userData.id);
            formData.append("client_id", userData.client_id);
            axios.post(Constants.API.STORE_RESULTS, formData, {
                headers: {
                    Accept: 'application/json',
                },
            });
            updateVitalSigns(vitalSignsResults.results);
            navigate('/results', { state: { data: vitalSignsResults.results } });
        } else {
            navigate('/results', { state: { data: vitalSignsResults.results } });
        }

        if (vitalSignsResults.results.pulseRate && clientDetails) {
            axios.post(Constants.API.REDUCE_SCAN_LIMIT, {
                client_id: clientDetails.id
            }, {
                headers: {
                    Accept: 'application/json',
                },
            });

            updateVitalSigns(vitalSignsResults.results);
            navigate('/results', { state: { data: vitalSignsResults.results } });
        }
    }, []);

    const onError = (errorData: AlertData) => {
        logger.error('sdk_session_error', {
            code: errorData?.code,
            codeName: codeName(errorData?.code),
            domain: errorData?.domain,
            memory: memorySnapshot(),
        });
        if (isUnmounted.current) return;
        setError(errorData);
    };

    const onWarning = (warningData: AlertData) => {
        logger.warn('sdk_session_warning', {
            code: warningData?.code,
            codeName: codeName(warningData?.code),
            domain: warningData?.domain,
        });
        if (isUnmounted.current) return;
        if (
            warningData.code ===
            HealthMonitorCodes.MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_WARNING
        ) {
            setVitalSigns(null);
        }
        setWarning(warningData);
    };

    const onStateChange = useCallback((state: SessionState) => {
        logger.info('sdk_session_state', { state: SessionState[state] ?? state });
        if (isUnmounted.current) return;
        setSessionState(state);
        if (state === SessionState.MEASURING) {
            setVitalSigns(null);
        }
    }, []);

    const onEnabledVitalSigns = useCallback((vitalSigns: EnabledVitalSigns) => {
        if (isUnmounted.current) return;
        setEnabledVitalSigns(vitalSigns);
    }, []);

    const onOfflineMeasurement = useCallback(
        (offlineMeasurements: OfflineMeasurements) => {
            if (isUnmounted.current) return;
            setOfflineMeasurements(offlineMeasurements);
        },
        [],
    );

    const onActivation = useCallback((activationId: string) => {
        // the device has been activated with activationId
    }, []);

    const onImageData = useCallback((imageValidity: ImageValidity) => {
        if (isUnmounted.current) return;
        let message: string;
        if (imageValidity != ImageValidity.VALID) {
            switch (imageValidity) {
                case ImageValidity.INVALID_DEVICE_ORIENTATION:
                    message = 'Unsupported Orientation';
                    break;
                case ImageValidity.TILTED_HEAD:
                    message = 'Head Tilted';
                    break;
                case ImageValidity.FACE_TOO_FAR: // Placeholder, currently not supported
                    message = 'You Are Too Far';
                    break;
                case ImageValidity.UNEVEN_LIGHT:
                    message = 'Uneven Lighting';
                    break;
                case ImageValidity.INVALID_ROI:
                default:
                    message = 'Face Not Detected';
            }
            setInfo({
                type: InfoType.INSTRUCTION,
                message: message,
            });
        } else {
            setInfoWithDismiss({ type: InfoType.NONE });
        }
    }, []);

    useEffect(() => {
        let isMounted = true;

        const initialize = async () => {
            for (let attempt = 0; ; attempt++) {
                const startedAt = performance.now();
                logger.info('sdk_init_start', { attempt: attempt + 1, memory: memorySnapshot() });
                try {
                    await monitor.initialize({
                        licenseKey,
                        productId,
                        licenseInfo: {
                            onEnabledVitalSigns,
                            onOfflineMeasurement,
                            onActivation,
                        },
                    });
                    logger.info('sdk_init_success', {
                        attempt: attempt + 1,
                        durationMs: Math.round(performance.now() - startedAt),
                    });
                    return;
                } catch (e) {
                    const canRetry =
                        isMounted &&
                        RETRYABLE_INIT_CODES.includes(e?.errorCode) &&
                        attempt < INIT_RETRY_DELAYS_MS.length;
                    logger.error('sdk_init_failed', {
                        attempt: attempt + 1,
                        willRetry: canRetry,
                        errorCode: e?.errorCode,
                        codeName: codeName(e?.errorCode),
                        durationMs: Math.round(performance.now() - startedAt),
                        error: describeError(e),
                        memory: memorySnapshot(),
                    });
                    if (!canRetry) {
                        throw e;
                    }
                    await wait(INIT_RETRY_DELAYS_MS[attempt]);
                }
            }
        };

        const run = initQueue.then(initialize);
        initQueue = run.catch(() => undefined);

        run
            .then(() => {
                if (isMounted) {
                    console.log(`Initialized monitor`);
                    setIsMonitorReady(true);
                    setError({ code: -1 });
                }
            })
            .catch((e) => {
                if (isMounted) {
                    setIsMonitorReady(false);
                    setError({ code: e?.errorCode });
                }
            });

        return () => {
            isMounted = false;
        };
    }, [licenseKey, productId]);

    useEffect(() => {
        (async () => {
            try {
                // cameraId is undefined until the cameras are listed. On a repeat scan
                // the SDK is already loaded, so initialize() finishes before that and
                // createFaceSession would fail with 1001 (no camera).
                if (!isMonitorReady || !processingTime || !video.current || cameraId === undefined) {
                    return;
                }

                try {
                    sessionRef.current?.terminate();
                } catch (e) {
                    logger.warn('sdk_session_terminate_failed', { error: describeError(e) });
                    console.error('Error terminating previous session', e);
                }

                // Forcing PORTRAIT makes the SDK reject every frame ("Unsupported
                // Orientation") when the screen is landscape: desktops, tablets held
                // sideways, browser device emulation. Otherwise let the SDK accept
                // the orientation the scan started in.
                const screenIsPortrait = window.matchMedia('(orientation: portrait)').matches;
                const orientation = screenIsPortrait ? DeviceOrientation.PORTRAIT : undefined;
                const options: FaceSessionOptions = {
                    input: video.current,
                    cameraDeviceId: cameraId,
                    processingTime,
                    onVitalSign,
                    onFinalResults,
                    onError,
                    onWarning,
                    onStateChange,
                    orientation,
                    onImageData,
                    // isAnalyticsEnabled: true,
                    /*******************************************************************************
                     * For accurate vital signs calculation the user's parameters should be provided.
                     * The following is an example of how the parameters are passed to the SDK:
                     *
                     * subjectDemographic: {sex: Sex.MALE, age: 35, weight: 75, height: 165}
                     *
                     * When measuring a new user then a new session must be created using the
                     * new user's parameters
                     * The parameters are used locally on the device only for the vital sign
                     * calculation, and deleted when the session is terminated.
                     *******************************************************************************/
                };

                const sessionStartedAt = performance.now();
                const faceSession = await monitor.createFaceSession(options);
                sessionsCreatedThisPage++;
                logger.info('sdk_session_created', {
                    durationMs: Math.round(performance.now() - sessionStartedAt),
                    sessionsCreatedThisPage,
                    hasCameraId: !!cameraId,
                    orientation: orientation === undefined ? 'any' : 'portrait',
                    screenOrientation: screen.orientation?.type,
                });
                console.log(`Session created`);
                sessionRef.current = faceSession;
                setSession(faceSession);
                setError({ code: -1 });
            } catch (e) {
                logger.error('sdk_session_create_failed', {
                    errorCode: e?.errorCode,
                    codeName: codeName(e?.errorCode),
                    hasCameraId: !!cameraId,
                    error: describeError(e),
                    memory: memorySnapshot(),
                });
                setError({ code: e.errorCode });
                console.error('Error creating a session', e);
            }
        })();
    }, [processingTime, isMonitorReady, cameraId]);

    // Release the camera and SDK processing when leaving the scan page,
    // otherwise the session keeps running in the background.
    useEffect(() => {
        return () => {
            isUnmounted.current = true;
            try {
                sessionRef.current?.terminate();
            } catch (e) {
                console.error('Error terminating session', e);
            }
            sessionRef.current = undefined;
        };
    }, []);

    useEffect(() => {
        if (startMeasuring) {
            if (sessionState === SessionState.ACTIVE) {
                session.start();
                setError({ code: -1 });
            }
        } else {
            sessionState === SessionState.MEASURING && session.stop();
        }
    }, [startMeasuring]);

    return {
        sessionState,
        vitalSigns: {
            pulseRate: {
                value: vitalSigns?.pulseRate?.value,
                isEnabled: enabledVitalSigns?.isEnabledPulseRate,
            },
            respirationRate: {
                value: vitalSigns?.respirationRate?.value,
                isEnabled: enabledVitalSigns?.isEnabledRespirationRate,
            },
            stress: {
                value: vitalSigns?.stressLevel?.value,
                isEnabled: enabledVitalSigns?.isEnabledStressLevel,
            },
            hrvSdnn: {
                value: vitalSigns?.sdnn?.value,
                isEnabled: enabledVitalSigns?.isEnabledSdnn,
            },
            spo2: {
                value: null, //TODO Spo2 is currently disabled by algo
                isEnabled: false, //enabledVitalSigns?.isEnabledSpo2,
            },
            bloodPressure: {
                value: vitalSigns?.bloodPressure?.value,
                isEnabled: enabledVitalSigns?.isEnabledBloodPressure,
            },
        },
        offlineMeasurements,
        error,
        warning,
        info,
    };
};

export default useMonitor;
